import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { setMessage } from './message';
import proformaService from '../services/proforma.service';

export const fetchProformaList = createAsyncThunk(
    'proforma/fetchProformaList',
    async ({ page, pagesize, search, groupId, ordering }, thunkAPI) => {
        try {
            const response = await proformaService.getProformaList(page, pagesize, search, groupId, ordering);
            return response.data;
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const fetchArchivedProformaList = createAsyncThunk(
    'proforma/fetchArchivedProformaList',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await proformaService.getArchiveProformaList(page, pagesize, search, ordering);
            return response.data;
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const fetchProformaById = createAsyncThunk(
    'proforma/fetchProformaById',
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await proformaService.getProformaById({ id });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchWorkOrderDropdown = createAsyncThunk(
    'proforma/fetchWorkOrderDropdown',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await proformaService.getWorkOrderDropdown(
                page,
                pagesize,
                search,
                ordering,
            );
            return response.data;
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const createProforma = createAsyncThunk(
    'proforma/createProforma',
    async ({ values }, thunkAPI) => {
        try {
            const response = await proformaService.addProforma(values);
            return response.data;
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const updateProforma = createAsyncThunk(
    'proforma/updateProforma',
    async ({ id, values }, thunkAPI) => {
        try {
            const response = await proformaService.updateProforma(id, values);
            return response.data;
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const archiveProforma = createAsyncThunk(
    'proforma/archiveProforma',
    async (id, thunkAPI) => {
        try {
            await proformaService.archiveProforma(id);
            return id;
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const unArchiveProforma = createAsyncThunk(
    'proforma/unArchiveProforma',
    async (id, thunkAPI) => {
        try {
            await proformaService.unArchiveProforma(id);
            return id;
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

const initialState = {
    data: [],
    list: [],
    totalPages: 0,
    currentPage: 1,
    workOrderCurrentPage: 1,
    totalRecord: 0,
    status: 'idle',
    error: null,
    loading: false,
    workOrderLoading: false,
    hasMore: true,
    workOrderHasMore: true,
};

const proformaSlice = createSlice({
    name: 'proforma',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        resetProformaData: (state) => {
            state.data = [];
        },
        resetWorkOrderList: state => {
            state.workOrderlist = []; // This will clear the list
        },
        resetProformaList: (state) => {
            state.list = [];
        },
        resetCurrentProformaPage: (state) => {
            state.currentPage = 1;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchProformaList.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchProformaList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentPage;
                state.data = action.payload.results.data || [];
                state.totalRecord = action.payload.count || 0;
                state.totalPages = action.payload.totalPages || 0;
                state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchProformaList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
            });

        builder
            .addCase(fetchWorkOrderDropdown.pending, state => {
                state.status = 'workOrderLoading';
            })
            .addCase(fetchWorkOrderDropdown.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentPage;
                state.workOrderdata = action.payload || []; // Update to match your API response
                state.totalRecord = action.payload.count || 0; // Handle count (total records)
                state.totalPages = action.payload.totalPages || 0; // Handle totalPages
                state.workOrderCurrentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.workOrderList = [...state.workOrderlist, ...action.payload.results];
                state.workOrderHasMore = !!action.payload.next;
            })
            .addCase(fetchWorkOrderDropdown.rejected, (state, action) => {
                state.status = 'failed';
                state.workOrderLoading = false;
            });

        builder
            .addCase(fetchArchivedProformaList.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchArchivedProformaList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentPage;
                state.data = action.payload.results || [];
                state.totalRecord = action.payload.count || 0;
                state.totalPages = action.payload.totalPages || 0;
                state.currentPage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchArchivedProformaList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
            });

        builder
            .addCase(fetchProformaById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProformaById.fulfilled, (state, action) => {
                state.loading = false;
                state.activeProfileRecord = action.payload;
            })
            .addCase(fetchProformaById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        builder
            .addCase(createProforma.pending, state => {
                state.status = 'loading';
            })
            .addCase(createProforma.fulfilled, (state, action) => {
                state.status = 'succeeded';
            })
            .addCase(createProforma.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });

        builder
            .addCase(updateProforma.pending, state => {
                state.status = 'loading';
            })
            .addCase(updateProforma.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const updatedProforma = action.payload;
                const index = state.data.findIndex(item => item.id === updatedProforma.id);
                if (index !== -1) {
                    state.data[index] = updatedProforma;
                }
            })
            .addCase(updateProforma.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });

        builder
            .addCase(archiveProforma.pending, state => {
                state.status = 'loading';
            })
            .addCase(archiveProforma.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(item => item.id !== action.payload);
                state.list = state.list.filter(item => item.id !== action.payload);
            })
            .addCase(archiveProforma.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });

        builder
            .addCase(unArchiveProforma.pending, state => {
                state.status = 'loading';
            })
            .addCase(unArchiveProforma.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(item => item.id !== action.payload);
            })
            .addCase(unArchiveProforma.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { resetProformaData, resetWorkOrderList, resetProformaList, resetCurrentProformaPage } = proformaSlice.actions;
export default proformaSlice.reducer;